import React, { useEffect, useMemo } from 'react';

const normalizeHttpUrl = (raw) => {
  const labelUrl = String(raw || '').trim();
  if (!labelUrl) return '';
  try {
    const u = new URL(labelUrl);
    const isHttp = u.protocol === 'http:' || u.protocol === 'https:';
    return isHttp ? u.toString() : '';
  } catch {
    return '';
  }
};

export default function LabelPrintModal({ open, labelUrl, onClose }) {
  const printableUrl = useMemo(() => normalizeHttpUrl(labelUrl), [labelUrl]);

  const isPdf = useMemo(() => {
    if (!printableUrl) return false;
    const low = printableUrl.toLowerCase();
    return low.includes('.pdf') || low.includes('application/pdf');
  }, [printableUrl]);

  const isImage = useMemo(() => {
    if (!printableUrl) return false;
    const low = printableUrl.toLowerCase();
    return low.includes('.png') || low.includes('.jpg') || low.includes('.jpeg') || low.includes('.gif') || low.includes('.webp');
  }, [printableUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (typeof onClose === 'function') onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <style>
        {`
          .label-print-overlay {
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.55);
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 12px;
          }
          .label-print-modal {
            width: min(1400px, 98vw);
            height: min(980px, 96vh);
            background: #ffffff;
            border-radius: 14px;
            box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
            border: 1px solid rgba(15, 23, 42, 0.12);
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
          .label-print-modal-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 10px 14px;
            border-bottom: 1px solid #e5e7eb;
          }
          .label-print-modal-header h3 {
            margin: 0;
            font-size: 16px;
            font-weight: 700;
            color: #111827;
          }
          .label-print-actions {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }
          .label-print-body {
            flex: 1;
            background: #ffffff;
            overflow: hidden;
            display: flex;
          }
          .label-print-frame {
            flex: 1;
            width: 100%;
            height: 100%;
            background: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .label-print-frame embed {
            width: 100%;
            height: 100%;
            border: 0;
            display: block;
            background: #ffffff;
          }
          .label-print-frame img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
            background: #ffffff;
          }
          @media print {
            body * {
              visibility: hidden !important;
            }
            .label-print-overlay,
            .label-print-overlay * {
              visibility: visible !important;
            }
            .label-print-overlay {
              position: static !important;
              inset: auto !important;
              padding: 0 !important;
              background: #ffffff !important;
            }
            .label-print-modal {
              width: 100% !important;
              height: 100vh !important;
              max-width: none !important;
              max-height: none !important;
              border: 0 !important;
              border-radius: 0 !important;
              box-shadow: none !important;
            }
            .label-print-modal-header {
              display: none !important;
            }
          }
        `}
      </style>

      <div className="label-print-overlay" role="dialog" aria-modal="true" onMouseDown={(e) => (e.target === e.currentTarget ? (typeof onClose === 'function' ? onClose() : null) : null)}>
        <div className="label-print-modal">
          <div className="label-print-modal-header">
            <h3>Print label</h3>
            <div className="label-print-actions">
              <button className="btn-submit" type="button" onClick={() => window.print()} disabled={!printableUrl}>
                Print
              </button>
              <button className="btn-secondary" type="button" onClick={() => (typeof onClose === 'function' ? onClose() : null)}>
                Close
              </button>
            </div>
          </div>

          <div className="label-print-body">
            {!printableUrl ? (
              <div style={{ padding: 16 }}>
                <div className="alert alert-error">Missing or invalid label url.</div>
              </div>
            ) : !(isPdf || isImage) ? (
              <div style={{ padding: 16 }}>
                <div className="alert alert-error">Unsupported label format.</div>
              </div>
            ) : (
              <div className="label-print-frame">
                {isPdf ? <embed src={printableUrl} type="application/pdf" /> : <img src={printableUrl} alt="Shipping label" />}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
